import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Alert, App, Button, Form, Input, InputNumber, Space, Switch, Typography } from 'antd'
import dayjs from 'dayjs'
import { type OrderGroup, type RentalOrder } from '@super-mega/server/contracts/order'
import { FormAnchorNav } from '@/components/form/form-anchor-nav.tsx'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { RENTAL_BATCH_FORM_SECTIONS, RENTAL_FORM_SECTIONS } from '@/features/order/const/rental-create-form.ts'
import { formatSalespersonOption } from '@/features/order/utils/service-order-form.ts'
import { useDebouncedValue } from '@/hooks/use-debounced-value.ts'
import { useTRPC } from '@/trpc/react.ts'
import { useRentalSubmission } from './hooks/use-rental-submission.ts'
import { OrderGroupSelectorModal } from './order-group-selector-modal.tsx'
import { OrderNotesEditModal } from './order-notes-edit-modal.tsx'
import { QuickCustomerModal, QuickSalespersonModal } from './order-ownership-quick-create-modal.tsx'
import { RentalAttachmentFields } from './rental-attachment-fields.tsx'
import { RentalContractFields } from './rental-contract-fields.tsx'
import { RentalEnterpriseFields } from './rental-enterprise-fields.tsx'
import type { RentalFormValues } from './rental-form.types.ts'
import type { RentalListSearch } from './rental-list-search.ts'
import { RentalPriceFields } from './rental-price-fields.tsx'
import { RentalResourceFields } from './rental-resource-fields.tsx'
import {
  RentalResourceSelectorModal,
  type RentalRoomRow,
  type RentalSiteRow
} from './rental-resource-selector-modals.tsx'
import { ServiceOrderCreateItemsFields } from './service-order-create-items-fields.tsx'
import { ServiceOrderCreateOwnershipFields } from './service-order-create-ownership-fields.tsx'
import type { CustomerOption, SalespersonOption, ServiceOrderItemFormValues } from './service-order-form.types.ts'
import { defaultServiceTerm } from './service-term-fields.tsx'
import { rentalBusinessPricing } from './utils/rental-business-pricing.ts'

export function RentalCreateForm({
  returnSearch,
  batch = false,
  initialCustomer,
  existingOrder,
  onRestore,
  canManageOrder = false,
  canCreateCustomer = false,
  canCreateBusiness = false,
  quickMemberTypes = []
}: {
  returnSearch: RentalListSearch
  batch?: boolean
  initialCustomer?: CustomerOption
  existingOrder?: RentalOrder
  onRestore?: () => void
  canManageOrder?: boolean
  canCreateCustomer?: boolean
  canCreateBusiness?: boolean
  quickMemberTypes?: Array<'employee' | 'agent'>
}) {
  const trpc = useTRPC()
  const { modal } = App.useApp()
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const isReadOnly = existingOrder?.status === 'active'
  const [creatingSalesperson, setCreatingSalesperson] = useState(false)
  const [createdCustomers, setCreatedCustomers] = useState<CustomerOption[]>([])
  const navigate = useNavigate()
  const [form] = Form.useForm<RentalFormValues>()
  const [selectedSalesperson, setSelectedSalesperson] = useState<SalespersonOption | undefined>(
    initialCustomer?.salesperson ?? undefined
  )
  const [selectedGroup, setSelectedGroup] = useState<OrderGroup | undefined>(() =>
    existingOrder?.orderGroupId
      ? {
          id: existingOrder.orderGroupId,
          name: existingOrder.orderGroupName ?? '',
          customerId: existingOrder.customerId,
          customerName: existingOrder.customerName,
          createdAt: existingOrder.createdAt,
          updatedAt: existingOrder.updatedAt,
          orderCount: 0,
          rentalOrderCount: 0,
          serviceOrderCount: 0
        }
      : undefined
  )
  const [selectingGroup, setSelectingGroup] = useState(false)
  const [selectingResource, setSelectingResource] = useState(false)
  const [selectedSite, setSelectedSite] = useState<RentalSiteRow | undefined>(() => {
    if (!existingOrder) {
      return undefined
    }
    const snapshot = existingOrder.addressSnapshot as Record<string, unknown> | null
    const text = (key: string) => (typeof snapshot?.[key] === 'string' ? snapshot[key] : '')
    return {
      id: existingOrder.siteId,
      name: existingOrder.siteName,
      provinceName: text('provinceName'),
      cityName: text('cityName'),
      districtName: text('districtName') || null,
      detailAddress: text('detailAddress'),
      isDisable: false,
      organization: { id: existingOrder.organizationId, name: existingOrder.organizationName, isDisable: false }
    }
  })
  const [selectedRoom, setSelectedRoom] = useState<RentalRoomRow | undefined>(() =>
    existingOrder?.roomId
      ? {
          id: existingOrder.roomId,
          siteId: existingOrder.siteId,
          roomNo: existingOrder.roomNo ?? '',
          floor: null,
          area: null,
          roomType: 'office',
          status: 'vacant',
          site: { id: existingOrder.siteId, name: existingOrder.siteName, organization: null }
        }
      : undefined
  )
  const [customerKeyword, setCustomerKeyword] = useState('')
  const [salespersonKeyword, setSalespersonKeyword] = useState('')
  const [businessKeyword, setBusinessKeyword] = useState('')
  const [serviceBusinessKeyword, setServiceBusinessKeyword] = useState('')
  const customerId = Form.useWatch('customerId', form)
  const businessId = Form.useWatch('businessId', form)
  const occupancyMode = Form.useWatch('occupancyMode', form)
  const includeServices = Form.useWatch('includeServices', form)
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({
      purpose: 'order.rental.read',
      keyword: useDebouncedValue(customerKeyword)
    }),
    staleTime: 60_000
  })
  const availableCustomers = [
    ...createdCustomers,
    ...(customers.data ?? []).filter((item) => !createdCustomers.some((created) => created.value === item.value))
  ]
  const salespeople = useQuery({
    ...trpc.lookup.members.queryOptions({
      purpose: 'order.rental.read',
      keyword: useDebouncedValue(salespersonKeyword)
    }),
    staleTime: 60_000
  })
  const businesses = useQuery({
    ...trpc.lookup.businesses.queryOptions({
      purpose: 'order.rental.read',
      keyword: useDebouncedValue(businessKeyword),
      type: 'lease'
    }),
    staleTime: 60_000
  })
  const serviceBusinesses = useQuery({
    ...trpc.lookup.businesses.queryOptions({
      purpose: 'order.rental.read',
      keyword: useDebouncedValue(serviceBusinessKeyword),
      type: 'valueAdded'
    }),
    enabled: Boolean(includeServices),
    staleTime: 60_000
  })
  const [enterprisePending, setEnterprisePending] = useState(false)
  const enterpriseSelection = Form.useWatch('enterpriseSelection', form)
  const { isSaving, busy, editSubmission, onFinish, onFinishFailed, saveWithContract } = useRentalSubmission({
    form,
    batch,
    existingOrder,
    selectedSite,
    selectedRoom,
    enterprisePending,
    returnSearch
  })
  const formDisabled = busy || isReadOnly
  const handleCustomerChange = (value: number, selectedCustomer?: CustomerOption) => {
    const selected =
      selectedCustomer ??
      availableCustomers.find((item) => item.value === value) ??
      (initialCustomer?.value === value ? initialCustomer : undefined)
    setSelectedSalesperson(selected?.salesperson ?? undefined)
    form.setFieldsValue({
      salespersonId: selected?.salesperson?.value,
      enterpriseSelection: { mode: 'none' },
      orderGroupId: undefined,
      orderGroupMode: 'none',
      orderGroupName: ''
    })
    form.setFieldValue(
      'items',
      form.getFieldValue('items').map((item: ServiceOrderItemFormValues) => ({
        ...item,
        subjectId: undefined,
        enterpriseSelection: undefined
      }))
    )
    setSelectedGroup(undefined)
    setSelectingGroup(false)
  }
  const openQuickCustomer = () => {
    if (enterpriseSelection && enterpriseSelection.mode !== 'none') {
      modal.confirm({
        title: '创建并切换客户？',
        content: '创建成功后将切换客户，并清空已选择的服务对象资料和订单组，其他订单内容保留。',
        onOk: () => setCreatingCustomer(true)
      })
    } else {
      setCreatingCustomer(true)
    }
  }
  const goBack = () => void navigate({ to: '/orders/rentals', search: returnSearch })

  let formContent = '执照与法人资料可稍后补齐，不影响建单；生成合同时可跳过补齐资料，未匹配字段以空格填充。'
  let formTitle = '新建租赁订单'
  if (existingOrder) {
    formTitle = `编辑租赁订单 · ${existingOrder.orderNo}`
    if (isReadOnly) {
      formContent = '订单生效中，只能编辑附件与备注。订单归属、租赁条款和合同设置已锁定。'
    } else {
      formContent = '修改订单归属、租赁条款和附件。历史合同和账单保留原始内容。已创建的附加服务在服务订单中单独编辑。'
    }
  }

  if (batch) {
    formTitle = '批量创建租赁订单'
    formContent =
      '同一客户、同一地址，每单自动分配一个不同的空闲房间。房源不足时整批失败，资料可在建单后补齐。价格按每个订单填写。'
  }

  let customerOptions = availableCustomers
  if (initialCustomer) {
    customerOptions = [initialCustomer, ...availableCustomers.filter((item) => item.value !== initialCustomer.value)]
  }
  if (existingOrder) {
    customerOptions = [
      { value: existingOrder.customerId, label: existingOrder.customerName },
      ...availableCustomers.filter((item) => item.value !== existingOrder.customerId)
    ]
  }

  let sections = RENTAL_FORM_SECTIONS
  if (existingOrder) sections = sections.filter((section) => section.id !== 'service-items')
  if (batch) sections = RENTAL_BATCH_FORM_SECTIONS

  return (
    <FormPageContainer breadcrumbRender={false} content={formContent} title={formTitle}>
      {existingOrder && editingNotes && (
        <OrderNotesEditModal
          kind="rental"
          order={existingOrder}
          onClose={() => {
            setEditingNotes(false)
            form.setFieldsValue({
              note: existingOrder.note ?? undefined,
              fileIds: existingOrder.attachments.map(({ file }) => file.id)
            })
          }}
        />
      )}
      {[customers, salespeople, businesses, serviceBusinesses].some((query) => query.isError) ? (
        <Alert className="mb-4" showIcon title="部分选项加载失败，请检查查询权限并重试" type="error" />
      ) : null}
      <Form<RentalFormValues>
        disabled={formDisabled}
        form={form}
        initialValues={{
          customerId: initialCustomer?.value,
          salespersonId: initialCustomer?.salesperson?.value,
          quantity: 1,
          billingInterval: 1,
          enterpriseSelection: existingOrder ? undefined : { mode: 'none' },
          contractType: 'lease',
          contractSettings: [{ contractType: 'lease' }],
          includeServices: false,
          items: [{ fileIds: [], note: '' }],
          leaseAction: 'setup',
          occupancyMode: 'physical',
          orderGroupMode: 'none',
          period: [dayjs().startOf('day'), dayjs().startOf('day').add(1, 'year')],
          rentType: 'monthly',
          ...(existingOrder
            ? {
                customerId: existingOrder.customerId,
                salespersonId: existingOrder.salespersonId,
                businessId: existingOrder.businessId ?? undefined,
                siteId: existingOrder.siteId,
                roomId: existingOrder.roomId ?? undefined,
                period: [dayjs(existingOrder.startAt), dayjs(existingOrder.endAt)],
                leaseAction: existingOrder.leaseAction,
                occupancyMode: existingOrder.occupancyMode,
                rentType: existingOrder.rentType,
                billingInterval: existingOrder.billingInterval,
                agreedPrice: Number(existingOrder.agreedPrice),
                referencePrice:
                  existingOrder.referencePrice === null ? undefined : Number(existingOrder.referencePrice),
                depositAmount: existingOrder.depositAmount == null ? null : Number(existingOrder.depositAmount),
                managementFee: existingOrder.managementFee == null ? undefined : Number(existingOrder.managementFee),
                waterUnitPrice:
                  existingOrder.waterUnitPrice === null ? undefined : Number(existingOrder.waterUnitPrice),
                electricityUnitPrice:
                  existingOrder.electricityUnitPrice === null ? undefined : Number(existingOrder.electricityUnitPrice),
                contractSettings: existingOrder.contractSettings ?? [
                  {
                    contractType: existingOrder.contractType ?? 'lease',
                    templateId: existingOrder.contractTemplateId ?? undefined
                  }
                ],
                contractType: existingOrder.contractType ?? 'lease',
                contractTemplateId: existingOrder.contractTemplateId ?? undefined,
                contractPartyKind: existingOrder.contractPartyKind ?? undefined,
                contractPartyName: existingOrder.contractPartyName ?? undefined,
                orderGroupMode: existingOrder.orderGroupId ? 'existing' : 'none',
                orderGroupId: existingOrder.orderGroupId ?? undefined,
                fileIds: existingOrder.attachments.map(({ file }) => file.id),
                note: existingOrder.note ?? undefined
              }
            : {})
        }}
        layout="vertical"
        onFinish={isReadOnly ? undefined : onFinish}
        onFinishFailed={onFinishFailed}
        scrollToFirstError={FORM_ERROR_SCROLL_OPTIONS}
      >
        <div className="flex w-full flex-col gap-5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            {batch && (
              <section
                id="section-rental-quantity"
                className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs"
              >
                <Typography.Title level={5}>生成订单数量</Typography.Title>
                <Form.Item
                  label="生成订单数量"
                  name="quantity"
                  rules={[{ required: true, type: 'integer', min: 1, max: 100, message: '请输入 1 至 100 的整数' }]}
                  extra="每批最多 100 单，每个订单分配不同房间；提交时统一校验房源，全部成功或全部失败。"
                >
                  <InputNumber className="w-full sm:w-64" min={1} max={100} precision={0} suffix="单" />
                </Form.Item>
              </section>
            )}
            <ServiceOrderCreateOwnershipFields
              description="客户是本次租赁的下单方，业务员负责跟进。可用订单组归集订单。"
              orderGroupHint="不加入时各订单独立；选择新建时，共用一个订单组，可自动生成订单组号。"
              onCreateCustomer={!existingOrder && canCreateCustomer ? openQuickCustomer : undefined}
              onCreateSalesperson={
                !existingOrder && quickMemberTypes.length ? () => setCreatingSalesperson(true) : undefined
              }
              customerId={customerId}
              customerLoading={customers.isFetching}
              customerOptions={customerOptions}
              disabled={formDisabled}
              customerLocked={Boolean(existingOrder && existingOrder.status !== 'pending')}
              hasEnterpriseSelection={Boolean(enterpriseSelection && enterpriseSelection.mode !== 'none')}
              onCustomerChange={handleCustomerChange}
              onCustomerSearch={setCustomerKeyword}
              onClearOrderGroup={() => {
                form.setFieldsValue({ orderGroupId: undefined, orderGroupMode: 'none', orderGroupName: '' })
                setSelectedGroup(undefined)
              }}
              onOpenOrderGroupSelector={() => setSelectingGroup(true)}
              onOrderGroupModeChange={(mode) => {
                form.setFieldsValue({ orderGroupId: undefined, orderGroupMode: mode, orderGroupName: '' })
                setSelectedGroup(undefined)
              }}
              onSalespersonSearch={setSalespersonKeyword}
              orderGroup={selectedGroup}
              salespersonLoading={salespeople.isFetching}
              salespersonOptions={
                existingOrder?.salespersonId
                  ? [
                      { value: existingOrder.salespersonId, label: existingOrder.salespersonName },
                      ...(salespeople.data ?? []).filter((item) => item.value !== existingOrder.salespersonId)
                    ]
                  : [
                      ...(selectedSalesperson ? [formatSalespersonOption(selectedSalesperson)] : []),
                      ...(salespeople.data ?? []).filter((item) => item.value !== selectedSalesperson?.value)
                    ]
              }
            />

            <RentalResourceFields
              batch={batch}
              canCreateBusiness={canCreateBusiness}
              businessOptions={businesses.data}
              businessLoading={businesses.isFetching}
              existingOrder={existingOrder}
              selectedSite={selectedSite}
              selectedRoom={selectedRoom}
              busy={formDisabled}
              onBusinessSearch={setBusinessKeyword}
              onBusinessChange={(value, createdOption) => {
                form.setFieldsValue({ siteId: undefined, roomId: undefined })
                setSelectedSite(undefined)
                setSelectedRoom(undefined)
                setSelectingResource(false)
                const option = createdOption ?? businesses.data?.find((item) => item.value === value)
                const prices = rentalBusinessPricing(option)
                form.setFieldValue('referencePrice', prices.referencePrice)
                form.setFieldValue('agreedPrice', prices.agreedPrice)
                form.setFieldValue('depositAmount', prices.depositAmount)
                form.setFieldValue('managementFee', prices.managementFee)
                form.setFieldValue('waterUnitPrice', prices.waterUnitPrice)
                form.setFieldValue('electricityUnitPrice', prices.electricityUnitPrice)
              }}
              onOccupancyChange={(value) => {
                if (value === 'registration') {
                  form.setFieldValue('roomId', undefined)
                  setSelectedRoom(undefined)
                }
              }}
              onOpenResource={() => setSelectingResource(true)}
            />

            {!batch && (
              <RentalEnterpriseFields
                existingOrder={existingOrder?.customerId === customerId ? existingOrder : undefined}
                busy={formDisabled}
                canManageOrder={canManageOrder && !isReadOnly}
                setEnterprisePending={setEnterprisePending}
                onSuccess={() => editSubmission.clear()}
              />
            )}
            <RentalPriceFields />
            <RentalContractFields />

            {batch ? (
              <section
                id="section-rental-notes"
                className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs"
              >
                <Typography.Title level={5}>备注</Typography.Title>
                <Form.Item label="备注" name="note" rules={[{ max: 5000 }]}>
                  <Input.TextArea rows={4} maxLength={5000} showCount placeholder="备注将应用到本批全部订单" />
                </Form.Item>
              </section>
            ) : (
              <RentalAttachmentFields isSaving={formDisabled} existingOrder={existingOrder} />
            )}

            {!existingOrder && !batch && (
              <section
                className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs"
                id="section-service-items"
              >
                <div className="mb-5">
                  <Typography.Title className="mb-1!" level={5}>
                    附加服务
                  </Typography.Title>
                  <Typography.Text type="secondary">按需添加代办等服务，每项会生成独立服务订单。</Typography.Text>
                </div>
                <Form.Item label="同时创建附加服务" name="includeServices" valuePropName="checked">
                  <Switch />
                </Form.Item>
                {includeServices ? (
                  <ServiceOrderCreateItemsFields
                    customerId={customerId}
                    inheritEnterprise
                    embedded
                    businessLoading={serviceBusinesses.isFetching}
                    businessOptions={serviceBusinesses.data ?? []}
                    disabled={isSaving}
                    onBusinessChange={(index, value) => {
                      const option = serviceBusinesses.data?.find((item) => item.value === value)
                      if (option?.defaultServiceMode === 'term') {
                        const term = defaultServiceTerm()
                        term.rules[0].unitPrice = Number(option.referencePrice ?? 0)
                        form.setFieldValue(['items', index, 'term'], term)
                        form.setFieldValue(['items', index, 'agreedAmount'], undefined)
                      } else {
                        form.setFieldValue(['items', index, 'term'], null)
                        form.setFieldValue(
                          ['items', index, 'agreedAmount'],
                          option?.referencePrice == null ? undefined : Number(option.referencePrice)
                        )
                      }
                    }}
                    onBusinessSearch={setServiceBusinessKeyword}
                  />
                ) : null}
              </section>
            )}
            <div className="border-border bg-surface/95 sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 shadow-xs backdrop-blur">
              <Button disabled={busy} onClick={goBack}>
                返回租赁列表
              </Button>
              {isReadOnly ? (
                canManageOrder && (
                  <Authorized permissions={['order.rental.update']}>
                    <Button disabled={busy} onClick={() => setEditingNotes(true)}>
                      编辑备注与附件
                    </Button>
                  </Authorized>
                )
              ) : (
                <Space wrap>
                  <Button
                    disabled={busy}
                    onClick={() => {
                      if (existingOrder) {
                        onRestore?.()
                        return
                      }
                      form.resetFields()
                      setSelectedSite(undefined)
                      setSelectedRoom(undefined)
                      setSelectedGroup(undefined)
                      setSelectingResource(false)
                      setSelectingGroup(false)
                    }}
                  >
                    {existingOrder ? '恢复已保存内容' : '重置'}
                  </Button>
                  <Button
                    htmlType="submit"
                    type={batch ? 'primary' : 'default'}
                    disabled={busy || enterprisePending}
                    loading={isSaving}
                  >
                    {batch ? '批量创建订单' : '保存订单'}
                  </Button>
                  {!batch && (
                    <Authorized permissions={['contract.document.create', 'contract.document.confirm']}>
                      <Button
                        disabled={busy || enterprisePending}
                        loading={isSaving}
                        type="primary"
                        onClick={saveWithContract}
                      >
                        保存并生成合同
                      </Button>
                    </Authorized>
                  )}
                </Space>
              )}
            </div>
          </div>
          <FormAnchorNav className="hidden lg:block" sections={sections} />
        </div>
      </Form>
      {creatingCustomer && (
        <QuickCustomerModal
          onClose={() => setCreatingCustomer(false)}
          onCreated={(customer) => {
            setCreatedCustomers((previous) => [...previous, customer])
            form.setFieldValue('customerId', customer.value)
            handleCustomerChange(customer.value, customer)
            setCustomerKeyword('')
          }}
        />
      )}
      {creatingSalesperson && (
        <QuickSalespersonModal
          types={quickMemberTypes}
          onClose={() => setCreatingSalesperson(false)}
          onCreated={(salesperson) => {
            setSelectedSalesperson(salesperson)
            form.setFieldValue('salespersonId', salesperson.value)
            setSalespersonKeyword('')
          }}
        />
      )}
      {selectingResource && businessId && (
        <RentalResourceSelectorModal
          siteOnly={batch}
          onCancel={() => setSelectingResource(false)}
          onSelect={(site, room) => {
            setSelectedSite(site)
            setSelectedRoom(room)
            form.setFieldsValue({ siteId: site.id, roomId: room?.id })
          }}
          selectedSite={selectedSite}
          selectedRoom={selectedRoom}
          businessId={businessId}
          requireRoom={!batch && occupancyMode === 'physical'}
        />
      )}
      <OrderGroupSelectorModal
        customerId={customerId}
        initialSelectedGroup={selectedGroup}
        onCancel={() => setSelectingGroup(false)}
        onSelect={(group) => {
          setSelectedGroup(group)
          form.setFieldsValue({ orderGroupId: group.id, orderGroupMode: 'existing', orderGroupName: undefined })
          setSelectingGroup(false)
        }}
        open={selectingGroup}
      />
    </FormPageContainer>
  )
}
